import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { MessageDescriptor } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { Tabs, Tooltip } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAgentLifecycle, type AgentLifecycle } from '../hooks/useAgentLifecycle';
import { usePresence } from '../hooks/usePresence';
import { errorMessage } from '../i18n/errorMessages';
import { DISABLED_LABEL } from '../lib/agent-state';
import {
  ApiError,
  DEFAULT_PROJECTION_STRATEGY,
  fetchAgent,
  fetchAgentConfig,
  fetchAgentRuntime,
  fetchRemoteNodes,
  getAgentChannels,
  revealAgentApiKey,
  rotateAgentApiKey,
  setAgentChannelProjectionStrategy,
  type Agent,
  type AgentChannelSummary,
  type AgentRuntime,
  type HostedAgentPlacement,
  type ProjectionStrategy,
} from '../lib/api';
import { copyToClipboard, readClipboard } from '../lib/clipboard';
import { routes } from '../lib/routes';
import { getBorgeeHttpBaseUrl } from '../lib/runtime-base-url';
import { useAgentStore, useAuthStore, useDmStore, useServerStore } from '../stores';
import {
  buildAgentConnectCommandParts,
  providerHasPlatformDifference,
  resolveAgentProvider,
  type AgentProvider,
  type Platform,
} from './AgentConnectResult';
import styles from './AgentDetailView.module.css';
import AgentDisabledBanner from './AgentDisabledBanner';
import AgentLifecycleSection from './AgentLifecycleSection';
import ChannelView from './ChannelView';
import DecommissionAgentModal from './DecommissionAgentModal';
import ManagedAgentProvisioningStatus, {
  ManagedAgentRuntimeConnection,
} from './ManagedAgentProvisioningStatus';
import PresenceDot from './PresenceDot';
import RuntimeCard from './RuntimeCard';
import { useToast } from './Toast';
import { UserAvatar } from './UserAvatar';
import { UserBadge } from './UserBadge';

function formatMaskedApiKey(last4: string): string {
  return `bgr_...${last4}`;
}

const API_KEY_AUTO_CLEAR_MS = 60_000;
const PROJECTION_STRATEGIES: ProjectionStrategy[] = [
  'session-brief',
  'turn-full',
  'message-only',
  'turn-thin',
  'file-brief',
];

const PROJECTION_STRATEGY_INFOTIP = [
  msg`session-brief: stable Borgee projection lives mainly in the Claude session brief.`,
  msg`turn-full: each turn carries the full Borgee projection inline.`,
  msg`message-only: ordinary turns pass through mostly the raw user message.`,
  msg`turn-thin: each turn keeps only a thin Borgee projection layer.`,
  msg`file-brief: stable Borgee projection is written to a provider-visible CLAUDE.md brief.`,
];

function resolveConnectedClaudeProvider(lastConnectedPluginId?: string): 'claude' | null {
  return lastConnectedPluginId === 'agents-host:claude' ? 'claude' : null;
}

// GET /agents/{id} answers 403 only for an agent someone else owns. Everything
// else it cannot serve is a 404, and that covers far more than a typo: the
// `/members/:id` route reaches this view for every colleague who is not the
// viewer, so a human account, a deleted agent and an unknown id all land here.
// Those two are settled answers; anything else is a failure that a second
// attempt can still turn into a profile.
interface ProfileFailure {
  text: MessageDescriptor;
  retryable: boolean;
}

function describeProfileFailure(err: unknown): ProfileFailure {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return { text: msg`Only its owner can see this agent.`, retryable: false };
    }
    if (err.status === 404) {
      return {
        text: msg`This member is a person, or an agent that no longer exists.`,
        retryable: false,
      };
    }
    return { text: errorMessage(err, msg`Could not load this Agent profile.`), retryable: true };
  }
  // A rejection that is not an ApiError never reached the server: it is the
  // browser's own TypeError, whose message is about fetch rather than about
  // this profile.
  return { text: msg`Could not reach the server.`, retryable: true };
}

interface Props {
  agentId: string;
  agent: Agent;
}

// Every piece of state below belongs to the agent the view is showing — the
// fetched profile, the DM channel, the revealed key — and none of it is reset in
// place, so the whole view is keyed on the agent id. Switching agents therefore
// mounts a fresh one instead of carrying the previous agent's data under the new
// agent's name, and no caller has to know that.
export default function AgentDetailView({ agentId, agent }: Props) {
  const { i18n } = useLingui();
  return <AgentDetail key={agentId} agentId={agentId} agent={agent} />;
}

function AgentDetail({ agentId, agent: agentProp }: Props) {
  const { i18n } = useLingui();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<'chat' | 'profile'>(
    tabParam === 'profile' ? 'profile' : 'chat',
  );
  const [dmChannelId, setDmChannelId] = useState<string | null>(null);
  const [dmFailed, setDmFailed] = useState(false);
  const [fetchedAgent, setFetchedAgent] = useState<Agent | null>(null);
  const [profileFailure, setProfileFailure] = useState<ProfileFailure | null>(null);
  const [profileAttempt, setProfileAttempt] = useState(0);
  const [showDecommission, setShowDecommission] = useState(false);
  const openDm = useDmStore((s) => s.openDm);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const agent = fetchedAgent ?? agentProp;
  const currentUser = useAuthStore((state) => state.currentUser);
  const ownerProfile =
    activeTab === 'profile' && currentUser?.role === 'member' && currentUser.id === agent.owner_id;

  // One authoritative copy of the agent for the whole view. The banner, the
  // profile hero and the lifecycle controls must never disagree about whether
  // the agent is switched off, so every write lands here.
  const applyAgent = useCallback((updated: Agent) => {
    setFetchedAgent(updated);
    // The sidebars render from the agent list, so a lifecycle write has to
    // reach it too or their rows keep the stale dot until the next navigation.
    useAgentStore.getState().loadAgents();
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchAgent(agentId)
      .then((loaded) => {
        if (cancelled) return;
        setFetchedAgent(loaded);
        setProfileFailure(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setProfileFailure(describeProfileFailure(err));
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, profileAttempt]);

  const lifecycle = useAgentLifecycle({
    agentId,
    agent,
    onUpdated: applyAgent,
    onToast: showToast,
  });

  const switchTab = (tab: 'chat' | 'profile') => {
    setActiveTab(tab);
    setSearchParams(
      (params) => {
        params.set('tab', tab);
        return params;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    const t = tabParam === 'profile' ? 'profile' : 'chat';
    setActiveTab(t);
  }, [tabParam]);

  useEffect(() => {
    let cancelled = false;
    openDm(agentId)
      .then((dm) => {
        if (!cancelled) setDmChannelId(dm.id);
      })
      .catch(() => {
        if (!cancelled) setDmFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, openDm]);

  return (
    <div className="agent-detail-view">
      <div className="agent-detail-header">
        <UserBadge
          userId={agent.id}
          displayName={agent.display_name}
          role="agent"
          avatarUrl={agent.avatar_url}
          size={32}
          state={agent.state}
          disabled={agent.disabled}
          showPresence={!ownerProfile}
        />
        {ownerProfile && (
          <ManagedAgentRuntimeConnection agentId={agentId} ownerId={agent.owner_id} />
        )}
      </div>
      {showDecommission && (
        <DecommissionAgentModal
          agentId={agentId}
          agentName={agent.display_name}
          agentState={agent.state}
          lastConnectedPluginId={agent.last_connected_plugin_id}
          onClose={() => setShowDecommission(false)}
          onDeleted={() => {
            useAgentStore.getState().loadAgents();
            const guildId = useAuthStore.getState().guildId;
            if (guildId)
              void Promise.resolve(useServerStore.getState().loadMembers(guildId)).catch(
                () => undefined,
              );
            showToast(msg`${agent.display_name} deleted`);
            if (guildId && window.location.pathname.includes('/members')) {
              navigate(routes.members(guildId));
            } else {
              navigate(routes.agents());
            }
          }}
        />
      )}

      <div className="channel-view-header">
        <div className="channel-view-tabs">
          <button
            className={`channel-view-tab${activeTab === 'chat' ? ' active' : ''}`}
            onClick={() => switchTab('chat')}
          >
            <Trans>Chat</Trans>
          </button>
          <button
            className={`channel-view-tab${activeTab === 'profile' ? ' active' : ''}`}
            onClick={() => switchTab('profile')}
          >
            <Trans>Profile</Trans>
          </button>
        </div>
      </div>

      <div className="agent-detail-content">
        <AgentDisabledBanner agentName={agent.display_name} lifecycle={lifecycle} />
        {activeTab === 'chat' && dmChannelId && (
          <ChannelView
            channelId={dmChannelId}
            hideHeader
            composerDisabled={agent.disabled || agent.state === 'offline'}
          />
        )}
        {activeTab === 'chat' && !dmChannelId && (
          <div className="no-channel">
            {dmFailed ? (
              <p>
                {agent.role === 'agent'
                  ? t(i18n)`Agent is not connected yet. Once it connects, you can chat here.`
                  : t(i18n)`Cannot open chat with this member.`}
              </p>
            ) : (
              <div className="loading-spinner-large" />
            )}
          </div>
        )}
        {activeTab === 'profile' && (
          <div className="agent-settings-scroll">
            {profileFailure ? (
              // No dismiss: behind this panel is the caller's placeholder agent,
              // and clearing the failure would render that stand-in as though the
              // profile had loaded — the one outcome this branch exists to avoid.
              <div className="no-channel" data-testid="agent-profile-unavailable">
                <div className="agent-inline-error" role="alert">
                  <strong>
                    <Trans>Can't show this profile.</Trans>
                  </strong>{' '}
                  {i18n._(profileFailure.text)}
                  {profileFailure.retryable && (
                    <div className="agent-inline-error-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        data-testid="agent-profile-retry"
                        onClick={() => setProfileAttempt((n) => n + 1)}
                      >
                        <Trans>Try again</Trans>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <AgentSettingsPanel
                agentId={agentId}
                agent={agent}
                lifecycle={lifecycle}
                onDecommission={() => setShowDecommission(true)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface SettingsPanelProps {
  agentId: string;
  agent: Agent;
  lifecycle: AgentLifecycle;
  onDecommission: () => void;
}

function AgentSettingsPanel({ agentId, agent, lifecycle, onDecommission }: SettingsPanelProps) {
  const { i18n } = useLingui();
  const currentUser = useAuthStore((s) => s.currentUser);
  const { showToast } = useToast();
  const navigate = useNavigate();
  // A rotate or reveal returns a fresher key than the loaded agent carries, so
  // it takes precedence until the agent is reloaded.
  const [revealedLast4, setRevealedLast4] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);
  const [hostingConfiguration, setHostingConfiguration] = useState<
    'loading' | 'direct' | 'managed' | 'unavailable'
  >('loading');
  const [hostedPlacement, setHostedPlacement] = useState<HostedAgentPlacement | null>(null);
  const [hostedComputerName, setHostedComputerName] = useState<string | null>(null);
  const [hostingDetailsError, setHostingDetailsError] = useState<MessageDescriptor | null>(null);
  const autoClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewerUserID = currentUser?.id ?? null;
  const isOwner = currentUser?.role === 'member' && viewerUserID === agent.owner_id;
  const last4 = revealedLast4 ?? agent.api_key_last4 ?? null;
  const disabled = lifecycle.disabled;

  useEffect(() => {
    let cancelled = false;
    const loadHostingDetails = async () => {
      let hosted: HostedAgentPlacement | null;
      try {
        const { blob } = await fetchAgentConfig(agentId);
        if (cancelled) return;
        hosted = blob.hosted ?? null;
      } catch (err) {
        if (cancelled) return;
        setHostingConfiguration('unavailable');
        setHostingDetailsError(errorMessage(err, msg`Could not load hosting details.`));
        return;
      }
      setHostedPlacement(hosted);
      setHostingConfiguration(hosted ? 'managed' : 'direct');
      setHostingDetailsError(null);
      if (!hosted) return;
      try {
        const nodes = await fetchRemoteNodes();
        if (cancelled) return;
        setHostedComputerName(
          nodes.find((node) => node.id === hosted.node_id)?.machine_name ?? hosted.node_id,
        );
      } catch (err) {
        if (cancelled) return;
        setHostedComputerName(null);
        setHostingDetailsError(errorMessage(err, msg`Could not load hosting details.`));
      }
    };
    void loadHostingDetails();
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  useEffect(() => {
    return () => {
      if (autoClearTimerRef.current) {
        clearTimeout(autoClearTimerRef.current);
      }
    };
  }, []);

  const handleCopyKey = async () => {
    setCopying(true);
    try {
      const key = await revealAgentApiKey(agentId);
      if (typeof key !== 'string' || key.length === 0) {
        showToast(msg`Copy failed`);
        return;
      }
      const ok = await copyToClipboard(key);
      if (!ok) return;
      setRevealedLast4(key.slice(-4));
      showToast(msg`API Key copied, auto-clears in 60s`);
      if (autoClearTimerRef.current) clearTimeout(autoClearTimerRef.current);
      const copiedKey = key;
      autoClearTimerRef.current = setTimeout(async () => {
        autoClearTimerRef.current = null;
        try {
          const current = await readClipboard();
          if (current === copiedKey) {
            await copyToClipboard('');
          }
        } catch {}
      }, API_KEY_AUTO_CLEAR_MS);
    } catch {
      showToast(msg`Copy failed`);
    } finally {
      setCopying(false);
    }
  };

  const handleRotateKey = async () => {
    try {
      const { api_key: key } = await rotateAgentApiKey(agentId);
      await copyToClipboard(key);
      setRevealedLast4(key.slice(-4));
      showToast(msg`New API Key copied`);
    } catch (err) {
      showToast(errorMessage(err, msg`Failed`));
    }
  };

  return (
    <div className="agent-page">
      <AgentProfileHero
        agent={agent}
        disabled={disabled}
        hostedPlacement={hostedPlacement}
        hostedComputerName={hostedComputerName}
        isOwner={isOwner}
      />

      {isOwner && (hostedPlacement || hostingDetailsError) && (
        <div className="agent-profile-field" data-testid="agent-hosted-details">
          <div className="agent-profile-field-label">
            <Trans>HOSTED BY COMPUTER</Trans>
          </div>
          {hostingDetailsError ? (
            <div className="agent-inline-note">{i18n._(hostingDetailsError)}</div>
          ) : (
            <>
              <div
                className="agent-profile-field-value"
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <span style={{ flex: 1 }}>{hostedComputerName ?? hostedPlacement?.node_id}</span>
                {hostedPlacement && (
                  <button
                    className="btn btn-sm"
                    data-testid="agent-open-computer"
                    onClick={() => navigate(routes.computer(hostedPlacement.node_id))}
                  >
                    <Trans>Open computer page</Trans>
                  </button>
                )}
              </div>
              <div className="agent-inline-note">
                <Trans>
                  Managed by this computer through{' '}
                  {hostedPlacement?.provider === 'claude'
                    ? 'Claude Code'
                    : hostedPlacement?.provider === 'codex'
                      ? 'Codex'
                      : 'GitHub Copilot'}
                  .
                </Trans>
              </div>
            </>
          )}
        </div>
      )}

      {isOwner && (
        <div className="agent-profile-field">
          <ManagedAgentProvisioningStatus
            agentId={agentId}
            ownerId={agent.owner_id}
            agentName={agent.display_name}
          />
        </div>
      )}

      {/* Copy stays live while disabled and Rotate does not: the UI blocks
          exactly what the server refuses. Revealing a key is a read, but the
          schema aborts any write of a new api_key onto a disabled row, so a
          live Rotate could only produce a 500. */}
      <div className="agent-profile-field">
        <div className="agent-profile-field-label">
          <Trans>API KEY</Trans>
        </div>
        <div
          className="agent-profile-field-value"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 14, flex: 1 }}>
            {last4 ? formatMaskedApiKey(last4) : t(i18n)`Loading...`}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-sm" onClick={handleCopyKey} disabled={copying}>
              {copying ? t(i18n)`Copying...` : t(i18n)`Copy`}
            </button>
            <button
              className="btn btn-sm"
              data-testid="agent-rotate-key"
              onClick={handleRotateKey}
              disabled={disabled}
              title={disabled ? t(i18n)`Enable the agent before rotating its key` : undefined}
            >
              <Trans>Rotate</Trans>
            </button>
          </div>
        </div>
        {disabled && (
          <div className="agent-inline-note">
            <Trans>
              The key can still be copied, but it will not authenticate while {agent.display_name}{' '}
              is disabled, and it cannot be rotated until you enable it again.
            </Trans>
          </div>
        )}
      </div>

      <ConnectInstructions
        agentName={agent.display_name}
        agentId={agentId}
        lastConnectedPluginId={agent.last_connected_plugin_id}
        disabled={disabled}
        hostedPlacement={hostedPlacement}
        hostedComputerName={hostedComputerName}
      />

      {(hostedPlacement?.provider ??
        resolveConnectedClaudeProvider(agent.last_connected_plugin_id)) === 'claude' && (
        <ProjectionStrategyCard agentId={agentId} />
      )}

      {hostingConfiguration === 'direct' && (
        <RegisteredAgentRuntime agentId={agentId} agent={agent} viewerUserID={viewerUserID} />
      )}

      <AgentLifecycleSection
        agentName={agent.display_name}
        isOwner={isOwner}
        lifecycle={lifecycle}
        onDecommission={onDecommission}
      />
    </div>
  );
}

const RegisteredAgentRuntime = ({
  agentId,
  agent,
  viewerUserID,
}: {
  agentId: string;
  agent: Agent;
  viewerUserID: string | null;
}) => {
  const [runtime, setRuntime] = useState<AgentRuntime | null>(null);
  const [attempt, setAttempt] = useState(0);
  const refresh = useCallback(() => setAttempt((value) => value + 1), []);
  useEffect(() => {
    let cancelled = false;
    fetchAgentRuntime(agentId)
      .then((value) => {
        if (!cancelled) setRuntime(value);
      })
      .catch(() => {
        if (!cancelled) setRuntime(null);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, attempt]);
  return (
    runtime && (
      <section className="agent-detail-card agent-detail-card-runtime">
        <RuntimeCard
          agent={agent}
          runtime={runtime}
          viewerUserID={viewerUserID}
          onRefresh={refresh}
        />
      </section>
    )
  );
};

interface ConnectProps {
  agentName: string;
  agentId: string;
  lastConnectedPluginId?: string;
  disabled: boolean;
  hostedPlacement: HostedAgentPlacement | null;
  hostedComputerName: string | null;
}

function ConnectInstructions({
  agentName,
  agentId,
  lastConnectedPluginId,
  disabled,
  hostedPlacement,
  hostedComputerName,
}: ConnectProps) {
  const { i18n } = useLingui();
  const [activeProvider, setActiveProvider] = useState<AgentProvider>(() =>
    resolveAgentProvider(agentId, lastConnectedPluginId),
  );
  const [pcPlatform, setPcPlatform] = useState<Platform>('windows');
  const serverUrl = getBorgeeHttpBaseUrl();
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [expandedWhileDisabled, setExpandedWhileDisabled] = useState(false);

  useEffect(() => {
    revealAgentApiKey(agentId)
      .then(setApiKey)
      .catch(() => {});
  }, [agentId]);

  useEffect(() => {
    if (!disabled) setExpandedWhileDisabled(false);
  }, [disabled]);

  const cmdBlocks = apiKey
    ? buildAgentConnectCommandParts(i18n, activeProvider, serverUrl, apiKey, agentName, pcPlatform)
    : [t(i18n)`Loading...`];

  const tabs: { key: AgentProvider; label: string }[] = [
    { key: 'claude', label: 'Claude Code' },
    { key: 'copilot', label: 'GitHub Copilot' },
    { key: 'codex', label: 'Codex' },
    { key: 'dsh', label: 'DeepSeek Harness' },
    { key: 'openclaw', label: 'OpenClaw' },
    { key: 'hermes', label: 'Hermes Agent' },
    { key: 'praestoclaw', label: 'PraestoClaw' },
    { key: 'kosmos', label: 'Kosmos Desktop' },
  ];

  if (hostedPlacement) {
    return (
      <div className="agent-profile-field" data-testid="agent-connect-instructions">
        <div className="agent-profile-field-label">
          <Trans>HOSTED RUNTIME</Trans>
        </div>
        <div className="agent-profile-field-value">
          {hostedComputerName ?? hostedPlacement.node_id}
        </div>
        <div className="agent-inline-note">
          <Trans>
            {agentName} is managed on that computer through{' '}
            {hostedPlacement.provider === 'claude'
              ? 'Claude Code'
              : hostedPlacement.provider === 'codex'
                ? 'Codex'
                : 'GitHub Copilot'}
            . Use the computer flow to create or remove hosted agents instead of running a manual
            connect command here.
          </Trans>
        </div>
      </div>
    );
  }

  // Collapse rather than delete while disabled. A disabled agent's key is
  // refused at connect time, so leaving a live "run this now" command in place
  // invites the owner to paste something that must fail and looks like a bug.
  // Deleting it outright would break the legitimate case of getting the
  // command ready before enabling the agent again.
  if (disabled && !expandedWhileDisabled) {
    return (
      <div className="agent-profile-field" data-testid="agent-connect-instructions">
        <div className="agent-profile-field-label">
          <Trans>CONNECT</Trans>
        </div>
        <div className="agent-connect-collapsed">
          <Trans>
            Connection instructions are hidden while {agentName} is disabled — a disabled agent's
            API key is refused at connect time, so the command below cannot bring it online. Enable
            it again first.
          </Trans>{' '}
          <div className="agent-connect-collapsed-actions">
            <button
              className="btn btn-sm"
              data-testid="agent-connect-show-anyway"
              onClick={() => setExpandedWhileDisabled(true)}
            >
              <Trans>Show instructions anyway</Trans>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-profile-field" data-testid="agent-connect-instructions">
      <div className="agent-profile-field-label">
        <Trans>CONNECT</Trans>
      </div>
      {disabled && (
        <div className="agent-inline-note" style={{ marginBottom: 10 }}>
          <Trans>
            {agentName} is disabled — this command will be refused until you enable it again.
          </Trans>
        </div>
      )}
      <div className={styles.providerTabs}>
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`btn btn-sm${activeProvider === t.key ? ' btn-primary' : ''}`}
            onClick={() => setActiveProvider(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {providerHasPlatformDifference(activeProvider) && (
        <div style={{ marginBottom: 4 }}>
          <Tabs
            size="small"
            activeKey={pcPlatform}
            onChange={(key: string) => setPcPlatform(key as Platform)}
            items={[
              { key: 'windows', label: 'Windows' },
              { key: 'linux', label: 'Linux / macOS' },
            ]}
          />
        </div>
      )}
      {cmdBlocks.map((block, i) => (
        <div
          key={i}
          style={{
            position: 'relative',
            background: 'var(--bg-secondary, #f5f5f5)',
            borderRadius: 6,
            padding: 10,
            fontSize: 12,
            fontFamily: 'var(--font-mono, monospace)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            marginBottom: 8,
          }}
        >
          {block}
          {apiKey && (
            <button
              className="btn btn-sm"
              style={{ position: 'absolute', top: 6, right: 6, fontSize: 11, padding: '2px 6px' }}
              onClick={() => copyToClipboard(block)}
              aria-label={t(i18n)`Copy command`}
            >
              <CopyOutlined aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function ProjectionStrategyCard({ agentId }: { agentId: string }) {
  const { i18n } = useLingui();
  const { showToast } = useToast();
  const [channels, setChannels] = useState<AgentChannelSummary[]>([]);
  const [drafts, setDrafts] = useState<Record<string, ProjectionStrategy>>({});
  const [loading, setLoading] = useState(true);
  const [savingChannelId, setSavingChannelId] = useState<string | null>(null);

  const loadChannels = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getAgentChannels(agentId);
      setChannels(rows);
      setDrafts(
        Object.fromEntries(
          rows.map((channel) => [
            channel.id,
            channel.projection_strategy ?? DEFAULT_PROJECTION_STRATEGY,
          ]),
        ),
      );
    } catch (err) {
      setChannels([]);
      setDrafts({});
      showToast(errorMessage(err, msg`Could not load projection strategy`));
    } finally {
      setLoading(false);
    }
  }, [agentId, showToast]);

  useEffect(() => {
    void loadChannels();
  }, [loadChannels]);

  const handleSave = async (channelId: string) => {
    const strategy = drafts[channelId];
    if (!strategy) {
      return;
    }
    setSavingChannelId(channelId);
    try {
      await setAgentChannelProjectionStrategy(agentId, channelId, strategy);
      await loadChannels();
      showToast(msg`Projection strategy saved`);
    } catch (err) {
      showToast(errorMessage(err, msg`Could not save projection strategy`));
    } finally {
      setSavingChannelId(null);
    }
  };

  return (
    <section
      className="agent-detail-card agent-detail-card-claude-channel-strategy"
      data-testid="agent-projection-strategy"
    >
      <div
        className="agent-profile-field-label"
        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <span>
          <Trans>PROJECTION STRATEGY</Trans>
        </span>
        <Tooltip
          title={
            <div style={{ display: 'grid', gap: 4 }}>
              {PROJECTION_STRATEGY_INFOTIP.map((line) => (
                <div key={line.id}>{i18n._(line)}</div>
              ))}
            </div>
          }
        >
          <span
            aria-label={t(i18n)`Projection strategy descriptions`}
            data-testid="agent-projection-strategy-infotip"
            title={PROJECTION_STRATEGY_INFOTIP.map((message) => i18n._(message)).join('\n')}
            style={{ display: 'inline-flex', color: 'var(--text-muted)', cursor: 'help' }}
          >
            <InfoCircleOutlined aria-hidden="true" />
          </span>
        </Tooltip>
      </div>
      <div className="agent-inline-note" style={{ marginTop: 4 }}>
        <Trans>
          One setting per top-level channel. Task threads inherit from the parent channel.
        </Trans>
      </div>
      {loading ? (
        <div className="agent-inline-note" style={{ marginTop: 8 }}>
          <Trans>Loading…</Trans>
        </div>
      ) : channels.length === 0 ? (
        <div className="agent-inline-note" style={{ marginTop: 8 }}>
          <Trans>Add this agent to a channel to manage projection strategy.</Trans>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          {channels.map((channel) => {
            const savedStrategy = channel.projection_strategy ?? DEFAULT_PROJECTION_STRATEGY;
            const draftStrategy = drafts[channel.id] ?? savedStrategy;
            const saving = savingChannelId === channel.id;
            return (
              <div
                key={channel.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <span>#{channel.name}</span>
                <select
                  className="input-field"
                  value={draftStrategy}
                  onChange={(event) =>
                    setDrafts((prev) => ({
                      ...prev,
                      [channel.id]: event.target.value as ProjectionStrategy,
                    }))
                  }
                  aria-label={t(i18n)`Projection strategy for ${channel.name}`}
                >
                  {PROJECTION_STRATEGIES.map((strategy) => (
                    <option key={strategy} value={strategy}>
                      {strategy}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn-sm"
                  disabled={saving || draftStrategy === savedStrategy}
                  onClick={() => handleSave(channel.id)}
                >
                  {saving ? t(i18n)`Saving...` : t(i18n)`Save`}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// The hero carries the agent's state, and delete is not here: it lives at the
// foot of the profile behind the lifecycle disclosure, so an AI colleague
// cannot be removed with one stray click on a bare icon.
function AgentProfileHero({
  agent,
  disabled,
  hostedPlacement,
  hostedComputerName,
  isOwner,
}: {
  agent: Agent;
  disabled: boolean;
  hostedPlacement: HostedAgentPlacement | null;
  hostedComputerName: string | null;
  isOwner: boolean;
}) {
  const { i18n } = useLingui();
  const live = usePresence(isOwner ? undefined : agent.id);
  const agentState = live?.state ?? agent.state;
  const reason = live?.reason ?? agent.reason;

  return (
    <>
      <div className="agent-profile-hero">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
          <UserAvatar
            userId={agent.id}
            displayName={agent.display_name}
            avatarUrl={agent.avatar_url}
            size={64}
            role="agent"
            state={agent.state}
            disabled={disabled}
            showPresence={!isOwner}
          />
          <div className="agent-profile-info">
            <div className="agent-profile-name">
              {agent.display_name}
              {disabled ? (
                <span className="agent-state-chip agent-state-chip-disabled">
                  {i18n._(DISABLED_LABEL)}
                </span>
              ) : isOwner ? (
                <ManagedAgentRuntimeConnection agentId={agent.id} ownerId={agent.owner_id} />
              ) : (
                <PresenceDot state={agentState} reason={reason} />
              )}
              {hostedPlacement && (
                <span
                  className="agent-state-chip agent-state-chip-hosted"
                  data-testid="agent-hosted-badge"
                >
                  <Trans>Hosted</Trans>
                </span>
              )}
            </div>
            <div className="agent-profile-slug">@{agent.id}</div>
          </div>
        </div>
      </div>

      <div className="agent-profile-field">
        <div className="agent-profile-field-label">
          <Trans>DISPLAY NAME</Trans>
        </div>
        <div className="agent-profile-field-value">{agent.display_name}</div>
      </div>

      <div className="agent-profile-field">
        <div className="agent-profile-field-label">
          <Trans>DESCRIPTION</Trans>
        </div>
        <div className="agent-profile-field-value agent-profile-field-dim">
          {(agent as any).description || t(i18n)`No description`}
        </div>
      </div>
    </>
  );
}
